import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import type { ResourceField } from '../../types';
import { ResourceFieldEditor } from './ResourceFieldEditor';

const RESOURCE_COLORS = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#64748b'];
const SCOPE_TYPES = ['global', 'board', 'canvas'] as const;

/**
 * Scope toggle buttons (Global / Board / Canvas).
 */
export function ScopeToggle({
  scopeType,
  onChange,
}: {
  scopeType: string;
  onChange: (scopeType: 'global' | 'board' | 'canvas') => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-3">
      <label className="block text-[10px] font-medium text-gray-500 mb-1">{t('resource.scope')}</label>
      <div className="flex gap-1">
        {SCOPE_TYPES.map((st) => (
          <button
            key={st}
            onClick={() => onChange(st)}
            className={`px-2 py-1 text-[10px] rounded border transition-colors ${
              scopeType === st
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t(`resource.scope${st.charAt(0).toUpperCase() + st.slice(1)}` as any)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Color picker with 8 preset colors.
 */
export function ColorPicker({
  color,
  onChange,
}: {
  color: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="mb-4">
      <label className="block text-[10px] font-medium text-gray-500 mb-1">Color</label>
      <div className="flex gap-1.5 flex-wrap">
        {RESOURCE_COLORS.map(c => (
          <button
            key={c}
            onClick={() => onChange(c)}
            style={{
              width: 20, height: 20, borderRadius: 4,
              backgroundColor: c,
              border: color === c ? '2px solid #1f2937' : '2px solid transparent',
              cursor: 'pointer',
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Fields list with add button.
 */
export function FieldsList({
  fields,
  onAddField,
  onUpdateField,
  onRemoveField,
}: {
  fields: ResourceField[];
  onAddField: () => void;
  onUpdateField: (fieldId: string, updates: Partial<ResourceField>) => void;
  onRemoveField: (fieldId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] font-medium text-gray-500">{t('resource.field.label')}</label>
        <button
          onClick={onAddField}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-blue-600 hover:bg-blue-50 rounded transition-colors"
        >
          <Plus size={10} />
          {t('resource.field.addField')}
        </button>
      </div>

      <div className="space-y-3">
        {fields.map((field) => (
          <ResourceFieldEditor
            key={field.id}
            field={field}
            onUpdate={(updates) => onUpdateField(field.id, updates)}
            onRemove={() => onRemoveField(field.id)}
          />
        ))}
      </div>

      {fields.length === 0 && (
        <div className="text-[10px] text-gray-400 text-center py-4">
          {t('resource.field.addField')}
        </div>
      )}
    </div>
  );
}
